<template>
	<template v-if="data">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<ButtonSticky @buttonclick="saveContact()" isSubmit>
				<template v-slot:icon><saveIcon /></template>
				<template v-slot:text> Speichern </template>
			</ButtonSticky>
			<section>
				<ButtonBack />

				<el-row class="flex flex--alignstart">
					<el-col :sm="12">
						<h1>{{ localTitle }}</h1>
					</el-col>
				</el-row>

				<TheBox :title="'Kontakt Daten'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
								<el-select v-model="data.gender" :placeholder="'Bitte wählen'">
									<el-option v-for="option in genderOptions" :key="option.value" :label="option.label" :value="option.value" />
								</el-select>
							</el-form-item>

							<el-form-item label="Titel (vor):" prop="titleBefore">
								<el-input v-model="data.titleBefore" :placeholder="'Bitte Titel eingeben'" />
							</el-form-item>

							<el-form-item label="Titel (nach):" prop="titleAfter">
								<el-input v-model="data.titleAfter" :placeholder="'Bitte Titel eingeben'" />
							</el-form-item>

							<el-form-item label="Vorname:" prop="firstName">
								<el-input v-model="data.firstName" :placeholder="'Bitte Vorname eingeben'" />
							</el-form-item>

							<el-form-item label="Nachname:" prop="lastName">
								<el-input v-model="data.lastName" :placeholder="'Bitte Nachname eingeben'" />
							</el-form-item>
						</el-col>
						<el-col :sm="12" :xs="24">
							<el-form-item label="E-Mail-Adresse:" prop="email">
								<el-input v-model="data.email" :placeholder="'Bitte Email eingeben'" />
							</el-form-item>

							<el-form-item label="Telefon" prop="phoneNumber">
								<el-input v-model="data.phoneNumber" :placeholder="'Bitte Telefonnummer eingeben'" />
							</el-form-item>

							<el-form-item label="Firma" prop="company">
								<el-input v-model="data.company" :placeholder="'Bitte Firma eingeben'" />
							</el-form-item>

							<el-form-item label="Erstellt am:" prop="street">
								<span>{{ $formatDate(data.createdDate) }}</span>
							</el-form-item>

							<el-form-item label="ID:" prop="street">
								<span>{{ data.hid }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>
			</section>
		</TheForm>

		<section class="no-marginbottom">
			<el-row>
				<el-col :sm="12" :xs="24">
					<h2>Gruppenzugehörigkeit</h2>
				</el-col>
				<el-col :sm="12" :xs="24" class="flex flex--justifyend">
					<TheButton @buttonclick="openDialogNewRole = true">
						<el-icon><Plus /></el-icon>Neue Gruppenzugehörigkeit
					</TheButton>
				</el-col>
			</el-row>
		</section>
		<RoleSlider :roles="data.roles ? data.roles : []" @delete="(role) => removeRoleFromUser(role)" />

		<section>
			<TheBox :id="'log'" :title="'Log'">
				<LogsTable :logs="logs" :loading="logsLoading" />
			</TheBox>
		</section>
		<DialogNewRole v-model:open="openDialogNewRole" :userId="id" :roleOptions="availableRoles" @reload="loadContact" />
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import BoxRole from '@/components/boxes/BoxRole.vue';
import DialogNewRole from '@/components/dialogs/DialogNewRole.vue';
import CasesTable from '@/components/tables/CasesTable.vue';
import LogsTable from '@/components/tables/LogsTable.vue';
import { getUser, getUserLogs, removeRoleFromUser, saveUser } from '@/requests/users';
import { categories } from '@/utils/categories';
import { returnFormOptions } from '@/utils/formOptions';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { ElMessage } from 'element-plus';
import { defineComponent, ref, markRaw } from 'vue';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { HistoryEntryResponse, SaveUserRequest, UserResponse } from '../types/types';
import { validateForm } from '../utils/functions';
import { texts } from '../utils/texts';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'KontaktDetailView',
	data() {
		return {
			data: null as UserResponse | null,
			formId: 'casesForm',
			rules: {
				gender: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur', 'change'] }],
				firstName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				lastName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				email: [
					{
						required: true,
						message: texts.formErrors.emailReq,
						trigger: 'blur',
					},
					{
						type: 'email',
						message: texts.formErrors.emailMissing,
						trigger: ['blur'],
					},
				],
			},
			waiting: false,
			loading: true,
			categories,
			openDialogNewRole: false,
			openDeleteContactModal: false,
			DialogNewRole,
			logs: [] as HistoryEntryResponse[],
			logsLoading: true,
			localTitle: '',
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		CasesTable,
		Plus,
		Delete,
		Loading,
		saveIcon,
		BoxRole,
		DialogNewRole,
		LogsTable,
		RoleSlider,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		availableRoles() {
			if (!this.data) return [];
			return returnFormOptions('contactRole').filter((role) => !this.data!.roles.some((userRole) => userRole.name === role.value?.name));
		},
	},
	methods: {
		async saveContact() {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const saveUserRequest: SaveUserRequest = {
				userId: this.data.id,
				email: this.data.email,
				firstName: this.data.firstName,
				lastName: this.data.lastName,
				gender: this.data.gender,
				postalCode: this.data.postalCode,
				city: this.data.city,
				street: this.data.street,
				phoneNumber: this.data.phoneNumber,
				company: this.data.company,
				titleBefore: this.data.titleBefore,
				titleAfter: this.data.titleAfter,
				initials: this.data.initials,
			};

			const result = await saveUser(saveUserRequest);
			if (result.success) {
				this.data = result.data;
				this.localTitle = this.data.firstName + ' ' + result.data.lastName;
				ElMessage({ message: this.texts.user.saveContactSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage({ message: this.texts.user.saveContactFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async loadContact() {
			const loading = startLoading('#kontakt');

			const result = await getUser(this.id);
			if (result.success) {
				this.data = result.data;
				this.localTitle = this.data.firstName + ' ' + result.data.lastName;
				this.loadLogs();
			} else {
				// handle error
			}

			stopLoading(loading);
		},
		async removeRoleFromUser(role) {
			const loading = startLoading('#kontakt');

			const removeResult = await removeRoleFromUser(this.id, role.name);
			if (removeResult.success) {
				await this.loadContact();
			} else {
				// handle error
			}

			stopLoading(loading);
		},
		async loadLogs() {
			this.logsLoading = true;
			const result = await getUserLogs(this.id);
			if (result.success) {
				this.logs = result.data;
			}

			this.logsLoading = false;
		},
	},
	mounted() {
		this.loadContact();
	},
	watch: {
		id() {
			this.loadContact();
		},
	},
});
</script>
